/**
 * The band's extra players: a brass section, an electric guitar, an acoustic one and a violin, each
 * on the styles that name it in `band` and at the levels the level table gives it. The brass plays
 * off the piano's chord strikes; the guitars strum a style's own `strum` figure, or the piano's chord
 * steps where it has none; the violin plays a figure now and then (chordViolin.js).
 *
 * A leaf, like chordPad.js, whose upper voices the brass plays.
 */

import { PLAYER_IDS } from './chartIntensity';
import { getPadVoicing } from './chordPad';
import {
  CHORD,
  DOWN,
  UP,
  getBlockStyle,
  getPatternSteps,
  getRhythmPattern,
  hasBandLayer,
  hasBassLine,
} from './chordRhythmPatterns';
import { getBlockGroove, getDrumGroove } from './drumGrooves';
import { getViolinNotes } from './chordViolin';
import { getGuitarShape, getPowerShape } from './guitarVoicing';

// The electric's palm-muted chugs, a second voice on the same player
export const MUTED_GUITAR_ID = 'band-muted-guitar';

/**
 * Each player, its clip track's name and its voice, a hidden preset tuned to sit under the band. A
 * note on another voice says so in its own `instrumentId`: the electric's chugs.
 */
export const BAND_LAYERS = [
  { id: 'brass', name: 'Brass', instrument: 'band-brass-section' },
  { id: 'guitar', name: 'Guitar', instrument: 'band-overdrive-guitar' },
  { id: 'acoustic', name: 'Acoustic', instrument: 'band-acoustic-guitar' },
  { id: 'violin', name: 'Violin', instrument: 'band-violin' },
];

// The longest chord the piano plays that the brass takes as a stab, in beats
const STAB_BEATS = 0.5;
// A stab is let go of short of its full length, so the next one speaks
const STAB_SHARE = 0.8;
// How many of the top strings an upstrum catches, and how much lighter it is
const UP_STRINGS = 4;
const UP_LEVEL = 0.85;
// Seconds between strings: a hard strum is tighter than a soft one
const SOFT_GAP = 0.016;
const HARD_GAP = 0.006;
// Each string lands a little lighter than the one before it, so a strum leans on where it starts
const STRING_FALLOFF = 0.03;
// How far a stroke may land late and a string's velocity may wander, for the hand's unevenness
const LATE = 0.004;
const WANDER = 0.06;
// The electric: a step this loud is an open accent, anything lighter a palm-muted chug
const ACCENT = 0.85;
const CHUG_GAP = 0.004;
const CHUG_SHARE = 0.6;
const CHUG_MAX = 0.15;

// The brass: the pad's three upper voices, led from the last chord
const brassVoicing = (chord, previous) => {
  const { upper } = getPadVoicing(chord, previous);

  return { voices: upper, previous: upper.length > 0 ? upper : previous };
};

const brassNotes = (strikes, pattern, { beatsPerBar, secondsPerBeat, velocity, intensity, thin, top }) => {
  const notes = [];
  let previous = null;

  strikes.forEach(({ chord, time, beats, melody }) => {
    if (melody) return;

    let voices;

    ({ voices, previous } = brassVoicing(chord, previous));
    if (top && voices.length > 0) voices = [...voices, voices[voices.length - 1] + 12];
    if (voices.length === 0) return;

    getPatternSteps(pattern, { beats, beatsPerBar, intensity, thin })
      .filter(({ play, hold }) => play === CHORD && hold <= STAB_BEATS)
      .forEach(({ at, hold, level }) =>
        voices.forEach((midi) =>
          notes.push({
            midi,
            time: time + at * secondsPerBeat,
            duration: Math.max(hold * STAB_SHARE * secondsPerBeat, 0.05),
            velocity: velocity * level,
          }),
        ),
      );
  });

  return notes;
};

// A repeatable wobble in -1..1, so the same chart is played the same way every time
const wobble = (time, string) => {
  const x = Math.sin((time * 1000 + string * 7.13) * 12.9898) * 43758.5453;

  return (x - Math.floor(x)) * 2 - 1;
};

// Down on the beat and up on the &, or on a sixteenth figure down on the even sixteenths
const isOnGrid = (at, grid) => Math.abs(at / grid - Math.round(at / grid)) < 1e-6;
const strokeOf = (at, grid) => (isOnGrid(at, grid * 2) ? DOWN : UP);

/**
 * One strum: the strings it catches in the order they sound, each at its own time and velocity.
 * Electric strokes are all down, on the power chord's strings.
 */
const strumStrings = (shape, { time, level, dir, gap, velocity }) => {
  const sounding = shape.map((midi, string) => ({ midi, string })).filter(({ midi }) => midi !== null);
  const strings = dir === UP ? sounding.slice(-UP_STRINGS).reverse() : sounding;
  const stroke = time + LATE * ((wobble(time, -1) + 1) / 2);
  const strength = dir === UP ? level * UP_LEVEL : level;

  return strings.map(({ midi, string }, index) => ({
    midi,
    string,
    time: stroke + gap * index,
    velocity: velocity * strength * (1 - STRING_FALLOFF * index) * (1 + WANDER * wobble(time, string)),
  }));
};

/**
 * A guitar's notes under a run of strikes. Each string sounds one note at a time, so a note rings
 * until its string is struck again or the chord ends: an upstrum leaves the low strings sounding.
 */
const guitarNotes = (id, strikes, pattern, { beatsPerBar, secondsPerBeat, velocity, intensity, thin }) => {
  const electric = id === 'guitar';
  const figure = pattern.strum || pattern.steps.filter(({ play }) => play === CHORD);
  // A figure off the eighths is strummed in sixteenths
  const grid = figure.every(({ at }) => isOnGrid(at, 0.5)) ? 0.5 : 0.25;
  const notes = [];

  strikes.forEach(({ chord, time, beats, melody }) => {
    if (melody) return;

    const shape = electric ? getPowerShape(chord) : getGuitarShape(chord);

    if (shape.length === 0) return;

    const end = time + beats * secondsPerBeat;
    const steps = getPatternSteps({ cycle: pattern.cycle, steps: figure }, { beats, beatsPerBar, intensity, thin });
    const hits = steps.flatMap(({ at, hold, level, dir }) => {
      const start = time + at * secondsPerBeat;
      const muted = electric && level < ACCENT;
      const strings = strumStrings(shape, {
        time: start,
        level,
        dir: electric ? DOWN : dir || strokeOf(at, grid),
        // The whole strum kept inside a sixteenth, so a fast tempo stays clean
        gap: Math.min(muted ? CHUG_GAP : SOFT_GAP - (SOFT_GAP - HARD_GAP) * level, secondsPerBeat / 4 / 5),
        velocity,
      });

      return muted
        ? strings.map((hit) => ({
            ...hit,
            instrumentId: MUTED_GUITAR_ID,
            cut: Math.min(hold * secondsPerBeat * CHUG_SHARE, CHUG_MAX),
          }))
        : strings;
    });

    hits.forEach(({ string, cut, ...hit }) => {
      const next = hits.reduce(
        (soonest, other) =>
          other.string === string && other.time > hit.time ? Math.min(soonest, other.time) : soonest,
        end,
      );
      const rings = next - hit.time;

      notes.push({ ...hit, duration: Math.max(Math.min(rings, cut ?? rings), 0.05) });
    });
  });

  return notes;
};

/**
 * One player's notes under a run of strikes: the brass on the piano's short chord stabs, the
 * guitars strumming, the violin's figures at the ends of phrases. Melody bars and rests have none.
 *
 * @param {string} id - One of BAND_LAYERS
 * @param {object[]} strikes - From getChordStrikes
 * @param {object} options - { patternId, pattern, beatsPerBar, secondsPerBeat, velocity, intensity, thin, top,
 *   bars, then, rises, following, variation }. `pattern` is played in place of the one `patternId`
 *   names: a block style. `top` adds the octave over the brass's top voice. `bars`, `then`, `rises`,
 *   `following` and `variation` are the violin's: see getViolinNotes.
 * @returns {Array<{midi: number, time: number, duration: number, velocity: number, instrumentId?: string}>}
 */
export const getBandNotes = (
  id,
  strikes,
  {
    patternId,
    pattern: given,
    beatsPerBar,
    secondsPerBeat,
    velocity,
    intensity,
    thin = false,
    top = false,
    bars = 0,
    then = null,
    rises = false,
    following = [],
    variation = 0,
  },
) => {
  const pattern = given || getRhythmPattern(patternId);

  if (!BAND_LAYERS.some((layer) => layer.id === id) || !hasBandLayer(pattern, id)) return [];

  if (id === 'violin') {
    const options = { bars, beatsPerBar, secondsPerBeat, velocity, intensity, then, rises, following, variation };

    return getViolinNotes(strikes, options);
  }

  const options = { beatsPerBar, secondsPerBeat, velocity, intensity, thin, top };

  return id === 'brass' ? brassNotes(strikes, pattern, options) : guitarNotes(id, strikes, pattern, options);
};

/**
 * The players a part in this style can switch on or off (PLAYER_IDS): those the style has, or under
 * block chords ('') the song's block style has.
 *
 * @param {string} rhythm - A pattern id, or '' for block chords
 * @param {object} options - `{ beatsPerBar, category }`, the song's
 */
export const getStylePlayers = (rhythm, { beatsPerBar = 4, category = '' } = {}) => {
  const style = rhythm ? getRhythmPattern(rhythm) : getBlockStyle(beatsPerBar, category);
  const drums = getDrumGroove(rhythm ? style.drums : getBlockGroove(beatsPerBar, category));
  const has = {
    bass: hasBassLine(style),
    drums: Boolean(drums),
    ...Object.fromEntries(BAND_LAYERS.map(({ id }) => [id, hasBandLayer(style, id)])),
  };

  return PLAYER_IDS.filter((id) => has[id]);
};
